import React, { useEffect, useState } from 'react';
import { Box, Inscription, PencilIcon, PlusIcon, ShimmerBox } from '@common-fe/common-fe';

import { LARGE_SIZE_LOGO, LetterIconSizeEnum, OrganizationTypes } from '@/common/constants';
import { Icon } from '@/components/elements/Icon';
import { UserLetterIcon } from '@/components/elements/LetterIcon/UserLetterIcon';
import theme from '@/styles/theme';

import { ContactPayload } from '../AddEditContactForm/stores/useContact.store';
import { ExpressSetup } from '../ExpressSetup.types';
import Tip from '../ExpressSetupBanner/Tip';
import { InfoTileIconWrapper, StyledWrapper } from '../styles/ExpressSetupBanner.styles';
import { useGetOrgFullAddress } from '../utils/getOrgFullAddress';

const MAX_CONTACTS = 3;

interface Props {
  expressSetupData?: ExpressSetup;
  isLoading?: boolean;
  visible?: boolean;
  handleOnSetAddressModalVisible?: (value: boolean) => void;
  handleOnClickMainContact?: () => void;
  handleOnClickOpenContactModal?: () => void;
  handleOnClickUpdateContact?: (value?: ContactPayload) => void;
}

export const ExpressSetupSummaryHeader: React.FC<Props> = ({
  expressSetupData,
  isLoading,
  visible,
  handleOnSetAddressModalVisible,
  handleOnClickMainContact,
  handleOnClickOpenContactModal,
  handleOnClickUpdateContact,
}) => {
  const [isOpeningShimmer, setIsOpeningShimmer] = useState(false);

  const fullAddress = useGetOrgFullAddress(expressSetupData?.address, true);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (visible) {
      isLoading && setIsOpeningShimmer(true);
      timeout = setTimeout(() => {
        setIsOpeningShimmer(false);
      }, 1000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [ visible, isLoading ]);
  return (
    <Box
      align="center"
      pad="spacing24"
      data-testid="ExpressSetupSummaryHeader"
    >
      <Box
        align="center"
        pad={{ top: 'spacing24', bottom: 'spacing48' }}
        width={{ min: '119px' }}
        height={{ min: '40px' }}
        data-testid="ExpressSetupSummaryHeader-Logo"
      >
        {isLoading || isOpeningShimmer
          ? (
            <ShimmerBox
              width="119px"
              height="40px"
              round="button2Round"
            />
          )
          :  (
            <Icon
              size={LetterIconSizeEnum.LOGO}
              text="name"
              height={LARGE_SIZE_LOGO.height}
              width={LARGE_SIZE_LOGO.width}
              src={expressSetupData?.logo}
            />
          )}
      </Box>
          
      {isLoading ? 
        (
          <ShimmerBox
            width="100%"
            height="70px"
            round="button2Round"
          />
        )
        : (
          <Inscription
            size="64px"
            color="textBody"
            weight="bold"
            style={{
              whiteSpace: 'normal',
              overflowWrap: 'break-word',
            }}
            lineHeight="70px"
            data-testid="ExpressSetupSummaryHeader-Name"
            textAlign="center"
          >
            {expressSetupData?.name}
          </Inscription>
        )}
  
      {isLoading ?
        (
          <ShimmerBox
            width="100%"
            height="20px"
            round="button2Round"
            margin={{ top: 'spacing12' }}
          />
        )
        : (
          <Box margin={{ top: 'xs' }} direction="row" align="center">
            <Inscription
              color="textBody"
              weight={400}
              style={{ lineHeight: '20px' }}
              data-testid="ExpressSetupSummaryHeader-FullAddress"
              textAlign="center"
            >
              {fullAddress}
            </Inscription>
            {
              !expressSetupData?.isAbandoned && (
                <Box
                  margin={{ left: 'xxxs' }}
                  onClick={() => handleOnSetAddressModalVisible && handleOnSetAddressModalVisible(true)}
                  style={{ cursor: 'pointer' }}
                  data-testid="ExpressSetupSummaryHeader-EditAddress"
                >
                  <PencilIcon size='18px' color="iconSecondary" />
                </Box>
              )
            }
          </Box>
        )}
  
      <Box direction="row" margin={{ top: 'l' }}>
        {expressSetupData?.contacts?.slice(0, MAX_CONTACTS)?.map((contact, index) => (
          <Box
            onClick={contact?.isPrimary
              ? handleOnClickMainContact
              : () => handleOnClickUpdateContact && handleOnClickUpdateContact(contact)}
            key={contact?.id}
            style={{ zIndex: 100 - index }}
            data-testid={`ExpressSetupSummaryHeader-Contact-${contact?.contactName}-${contact?.isPrimary ? 'primary' : 'secondary'}`}
            {...index ? { margin: { left: '-8px' } } : {}}
          >
            <Box title={contact?.contactName}>
              <UserLetterIcon
                testId='contact-info-icon'
                type={OrganizationTypes.partner}
                size={LetterIconSizeEnum.LOGO}
                text={contact?.contactName}
              />
            </Box>
          </Box>
        ))}
        {typeof expressSetupData?.contacts?.length === 'number' && expressSetupData?.contacts?.length > MAX_CONTACTS ? (
          <Tip items={expressSetupData?.contacts?.map((contact) => contact?.contactName || '').slice(MAX_CONTACTS)} space="42px">
            <InfoTileIconWrapper
              height="40px"
              width={{ min: '40px' }}
              margin={{ left: '-8px' }}
              style={{ zIndex: 1 }}
              justify="center"
              align="center"
              background="partner"
              hasBorder
            >
              <Inscription
                size="16px"
                color="textOnColor"
                weight={700}
              >
                +{expressSetupData?.contacts?.length - MAX_CONTACTS}
              </Inscription>
            </InfoTileIconWrapper>
          </Tip>
        ) : null}
        {!expressSetupData?.isAbandoned && (
          <StyledWrapper
            height="40px"
            width={{ min: '40px' }}
            margin={{ left: '-8px' }}
            justify="center"
            align="center"
            style={{ 
              zIndex: 0, 
              cursor: 'pointer',
              position: 'relative',
            }}
            background={{ color: 'background3', opacity: 0.1 }}
            onClick={!expressSetupData?.contacts?.length ? handleOnClickMainContact : handleOnClickOpenContactModal}
            data-testid="ExpressSetupSummaryHeader-AddContact"
          >
            <PlusIcon size="16px" color={theme.colors.background3} />
          </StyledWrapper>
        )}
      </Box>
    </Box>
  );
};
